<template>
  <div class="storyblok-scrollytelling-hero" :class="{ 'ignore-background': ignore_background }">
    <UtilWrapperLayout>
      <UtilGrid>
        <div
          ref="firstColumnRef"
          class="storyblok-scrollytelling-hero__column"
          :class="{ 'no-images': !main_image?.filename, 'no-text': !text_title && !text_top_title && !text_title_highlight && !text_top_title_highlight && !text?.content?.[0]?.content && !link?.cached_url }"
        >
          <div ref="firstTextColumnRef" class="storyblok-scrollytelling-hero__column-inner" :style="{ '--padding-bottom-distance': `${heightDistance}px` }">
            <AtomRichtext
              v-if="main_pre_title"
              :html="main_pre_title"
              class="storyblok-scrollytelling-hero__title-pretitle"
            />
            <div class="storyblok-scrollytelling-hero__title">
              <AtomRichtext
                :html="main_title"
                :class="{ 'is-small-title': small_main_title }"
              />
              <AtomRichtext
                ref="titleHighlightRef"
                :html="main_title_highlight"
                class="storyblok-scrollytelling-hero__title-highlight"
                :class="{ 'is-small-title': small_main_title }"
              />
            </div>
            <div v-if="main_image?.filename" class="storyblok-scrollytelling-hero__image">
              <AtomImage
                class="storyblok-scrollytelling-hero__image-inner"
                :image="main_image"
                provider="storyblok"
              />
            </div>
          </div>
        </div>
        <div
          v-show="text_title
            || text_top_title
            || text_title_highlight
            || text_top_title_highlight
            || text?.content?.[0]?.content
            || link?.cached_url"
          ref="secondColumnRef"
          class="storyblok-scrollytelling-hero__column"
          :class="{ 'no-images': gallery.length === 0, 'no-text': !text_title && !text_top_title && !text_title_highlight && !text_top_title_highlight && !text?.content?.[0]?.content && !link?.cached_url }"
        >
          <div class="storyblok-scrollytelling-hero__text-wrapper">
            <div ref="secondTextColumnRef" class="storyblok-scrollytelling-hero__text-inner">
              <div class="storyblok-scrollytelling-hero__text-top">
                <AtomRichtext
                  :html="text_top_title"
                  class="storyblok-scrollytelling-hero__richtext-pretitle"
                />
                <AtomRichtext
                  class="storyblok-scrollytelling-hero__richtext-title"
                  :html="text_title"
                />
                <AtomRichtext
                  class="storyblok-scrollytelling-hero__richtext-title is-highlight"
                  :html="text_title_highlight"
                />
              </div>
              <div class="storyblok-scrollytelling-hero__text-bottom">
                <AtomRichtext
                  :html="text"
                />
              </div>
              <div class="storyblok-scrollytelling-hero__text-link">
                <ProviderColorSchema color-schema="white">
                  <AtomLink
                    v-if="cachedUrlWithoutLanguage"
                    :icon="linkdata.icon"
                    :link="linkdata"
                    :text="link_label"
                    :filter-query-prop="filterQuery"
                  />
                </ProviderColorSchema>
              </div>
            </div>
          </div>
          <div class="storyblok-scrollytelling-hero__gallery">
            <UtilParallax
              v-for="(image, index) in gallery"
              :key="image._uid"
              class="storyblok-scrollytelling-hero__gallery-image"
              :distance="distances[index % 4]"
              :overscroll-distance="0"
              :breakpoints="{
                tablet: {
                  distance: distances[index % 4],
                  overscrollDistance: 0,
                },
                desktop: {
                  distance: distances[index % 4],
                  overscrollDistance: 0,
                },
              }"
            >
              <AtomImage
                provider="storyblok"
                :image="image.image"
                :aspect-ratio="image.aspectRatio"
              />
            </UtilParallax>
          </div>
        </div>
      </UtilGrid>
    </UtilWrapperLayout>
  </div>
</template>

<script setup>

const props = defineProps({
    main_title: {
        type: String,
        default: '',
    },

    main_pre_title: {
        type: String,
        default: '',
    },

    small_main_title: {
        type: Boolean,
        default: false,
    },

    main_title_highlight: {
        type: String,
        default: '',
    },

    main_image: {
        type: Object,
        default: () => {},
        validator: (value) => typeof value === 'object',
    },

    text_title: {
        type: String,
        default: '',
    },

    text_top_title: {
        type: String,
        default: '',
    },

    text_top_title_highlight: {
        type: String,
        default: '',
    },

    text_title_highlight: {
        type: String,
        default: '',
    },

    text: {
        type: Object,
        default: () => {},
        validator: (value) => typeof value === 'object',
    },

    link: {
        type: Object,
        default: () => {},
        validator: (value) => typeof value === 'object',
    },

    link_label: {
        type: String,
        default: '',
    },

    filterQuery: {
        type: Array,
        default: () => [],
    },

    gallery: {
        type: Array,
        default: () => [],
        validator: (value) => value.length > 0,
    },

    ignore_background: {
        type: Boolean,
        default: false,
    },
});

const linkdata = computed(() => {
    const compiledLink = useStoryblokLink(props.link, props.link_label);
    return compiledLink;
});

const { locale } = useI18n();
const cachedUrlWithoutLanguage = computed(() => props.link.cached_url.replace(`/${locale.value}/`, ''));

const colorTopLevel = inject('colorSchema');
const color6 = computed(() => `var(--c-${colorTopLevel.value}-6)`);
const color8 = computed(() => `var(--c-${colorTopLevel.value}-8)`);
const color2 = computed(() => `var(--c-${colorTopLevel.value}-2)`);
const color1 = computed(() => `var(--c-${colorTopLevel.value}-1)`);

const clientDeviceStore = useClientDeviceStore();

const firstColumnRef = ref(null);
const secondColumnRef = ref(null);
const titleHighlightRef = ref(null);
const parallaxOffsets = ref([]);
const secondTextColumnRef = ref(null);
const firstTextColumnRef = ref(null);

const distances = ['15vh', '-20vh', '-10vh', '-5vh'];

const heightDistance = ref(0);

onMounted(() => {
    const { createScrollTrigger } = useScrollTrigger();

    // calculate the height distance between the two text columns
    const firstTextColumnHeight = firstTextColumnRef.value.getBoundingClientRect().height;
    const secondTextColumnHeight = secondTextColumnRef.value.getBoundingClientRect().height;
    const height = secondTextColumnHeight - firstTextColumnHeight;
    if (height > 0) {
        heightDistance.value = height;
    }

    // Initialize parallax offsets and random X positions
    parallaxOffsets.value = props.gallery.map(() => 0);

    // Image animation to align with title highlight
    if (props?.main_image?.filename) {
        createScrollTrigger({
            trigger: firstColumnRef.value,
            start: 'top top',
            end: 'bottom center',
            scrub: true,
            onUpdate: (self) => {
                if (firstColumnRef.value) {
                    const image = firstColumnRef.value
                        .querySelector('.storyblok-scrollytelling-hero__image');
                    if (image) {
                        const { progress } = self;
                        /* eslint-disable-next-line */
                        const titleHighlightRect = titleHighlightRef.value.$el.getBoundingClientRect();
                        const distanceToAnimate = titleHighlightRect.height / 2 + 180;
                        const multiplier = clientDeviceStore.isMobile ? 0.5 : 5;
                        const animationProgress = Math.min(progress * multiplier, 1);
                        image.style.transform = `translateY(${animationProgress * distanceToAnimate * -1}px)`;
                    }
                }
            },
        });
    }
});
</script>

<style lang="scss">
.storyblok-scrollytelling-hero {
    @include default-content-columns;

    background-color: v-bind(color6);

    &.ignore-background {
        background-color: transparent;
    }
}
.storyblok-scrollytelling-hero__title {
    @include grid-columns(1, 7);

    .atom-richtext {
        width: min-content;
        width: 100%;
        color: var(--c-white);
        font-family: var(--f-family--black);
        white-space: pre;
        word-break: auto-phrase;

        .ignore-background & {
            color: v-bind(color2);
        }

        @include fluid('font-size', 96px, 174px);
        @include fluid('line-height', 105px, 183px);

        &.is-small-title {
            @include fluid('font-size', 56px, 126px);
            @include fluid('line-height', 60px, 130px);
        }
    }

    .storyblok-scrollytelling-hero__title-highlight {
        position: relative;
        z-index: 100;

        &.atom-richtext {
            color: v-bind(color2);
            white-space: pre-wrap;

            .ignore-background & {
                color: v-bind(color8);
            }
        }
    }
}
.storyblok-scrollytelling-hero__image {
    margin-top: 180px;
    margin-left: calc(var(--grid-offset) * -1);

    @include tablet {
        margin-left: 0;
    }

    @include mobile {
        margin-top: 45px
    }
}
.storyblok-scrollytelling-hero__column {
    @include grid-columns(1, 7);

    &:first-child {
        .storyblok-scrollytelling-hero__column-inner {
            @include fluid('padding-top', 50px, 150px);
            position: sticky;
            top: 156px;
            padding-bottom: 10vh;
            margin-bottom: var(--padding-bottom-distance);

            @include mobile {
                padding-bottom: 0;
                margin-bottom: 0;
            }
        }

        &.no-images {
            @include fluid('margin-bottom', 0px, 150px);

            @include mobile {
                margin-bottom: 0;
            }
        }

        &.no-text .storyblok-scrollytelling-hero__column-inner {
            @include mobile {
                margin-bottom: 50px;
            }
        }
    }

    &:last-child {
        @include grid-columns(8, 11);
        @include grid-layout(11);

        &.no-images {
            padding-top: 0vh;
            padding-bottom: 0vh;
            @include fluid('margin-bottom', 0px, 150px);

            @include mobile {
                padding-top: 0;
                padding-bottom: 10vh;
                margin-bottom: 100px;
            }
        }

        @include mobile {
            @include grid-layout(3);
            padding-top: 0;
            padding-bottom: 50vh;
        }
    }
}

.storyblok-scrollytelling-hero__gallery {
    @include grid-columns(1, 11);
    @include grid-layout(11);

    @include mobile {
        @include grid-layout(3);
        @include grid-columns(1, 3);
    }
}
.storyblok-scrollytelling-hero__text-wrapper {
    @include grid-columns(2, 8);
    position: relative;
    z-index: 10;
    display: flex;
    min-height: 100vh;
    flex-direction: column;
    justify-content: center;
    padding-top: var(--header-height);
    padding-bottom: calc(var(--header-height) / 2);

    @include mobile {
        padding-top: 0;
    }

    .no-images & {
        min-height: 50vh;

        @include mobile {
            min-height: 0;
        }
    }
}
.storyblok-scrollytelling-hero__text-inner {
    position: sticky;
    z-index: 100;
    top: 246px;

    .no-images & {
        position: relative;
        top: 146px;

        @include mobile {
            top: 0;
        }
    }

    @include mobile {
        top: 100px;
    }
}
.storyblok-scrollytelling-hero__richtext-pretitle {
    color: v-bind(color1);
    font-family: var(--f-family--bold);
    @include fluid('font-size', 16px, 20px);
    @include fluid('line-height', 24px, 31px);

    @include mobile {
        display: none;
    }
}
.storyblok-scrollytelling-hero__richtext-title {
    color: v-bind(color1);
    font-family: var(--f-family--black);
    @include fluid('font-size', 30px, 66px);
    @include fluid('line-height', 38px, 76px);

    &.is-highlight {
        color: var(--c-white);

        .ignore-background & {
            color: v-bind(color8);
        }
    }

    .ignore-background & {
        color: v-bind(color2);
    }
}
.storyblok-scrollytelling-hero__text-bottom {
    @include fluid('margin-top', 15px, 30px);
    @include fluid('margin-bottom', 15px, 30px);
}
.storyblok-scrollytelling-hero__text-link {
    font-family: var(--f-family--bold);
    @include fluid('font-size', 16px, 20px);
    @include fluid('line-height', 24px, 24px);

    .atom-link__text {
        color: var(--c-white);
    }
}
.storyblok-scrollytelling-hero__gallery-image {
    position: relative;
    will-change: transform;

    &:nth-child(4n) {
        @include grid-columns(5, 9);
    }

    // Third item
    &:nth-child(4n-1) {
        @include grid-columns(1, 6);
    }

    // Second item
    &:nth-child(4n-2) {
        @include grid-columns(7, 5);
    }

    // First item
    &:nth-child(4n-3) {
        @include grid-columns(1, 7);
    }

    @include mobile {
        &:nth-child(4n-3) {
            @include grid-columns(1, 2, false);
        }

        &:nth-child(4n-2) {
            @include grid-columns(2, 2, false);
        }

        &:nth-child(4n-1) {
            @include grid-columns(1, 2, false);
        }

        &:nth-child(4n) {
            @include grid-columns(2, 2, false);
        }
    }
}

.storyblok-scrollytelling-hero__title-pretitle {
    @include fluid('font-size', 16px, 28px);
    @include fluid('line-height', 24px, 38px);
    color: v-bind(color2);
    font-family: var(--f-family--black);
}
</style>
